<template>
  <div class="home-page">
    <template v-for="section in renderSections">
      <div v-if="section.type === 'banner'" :key="'banner-' + section.idx" class="home-banner">
        <div
          v-for="(banner, bIdx) in section.items"
          v-show="bIdx === getBannerActive(section.idx)"
          :key="banner.id"
          class="banner-item"
          @click="handleItemClick(banner)"
        >
          <img :src="banner.image" class="banner-image" :alt="banner.configName" />
        </div>
        <div v-if="section.items.length > 1" class="banner-dots">
          <span
            v-for="(banner, bIdx) in section.items"
            :key="banner.id"
            class="banner-dot"
            :class="{ active: bIdx === getBannerActive(section.idx) }"
            @click="setBannerActive(section.idx, bIdx)"
          ></span>
        </div>
      </div>

      <div
        v-else-if="section.type === 'nav'"
        :key="'nav-' + section.idx"
        class="home-nav"
        :style="{ gridTemplateColumns: `repeat(${section.columns}, 1fr)` }"
      >
        <div
          v-for="nav in section.items"
          :key="nav.id"
          class="nav-item"
          @click="handleItemClick(nav)"
        >
          <img v-if="nav.image" :src="nav.image" class="nav-icon" :alt="nav.configName" />
          <div class="nav-name">{{ nav.configName }}</div>
        </div>
      </div>

      <div v-else-if="section.type === 'product' || section.type === 'article'" :key="'group-' + section.idx" class="home-group">
        <div v-if="section.tabs.length > 1" class="group-tabs">
          <div
            v-for="tab in section.tabs"
            :key="tab.key"
            class="group-tab"
            :class="{ active: tab.key === getActiveTabKey(section.idx, section.tabs) }"
            @click="setActiveTab(section.idx, tab.key)"
          >
            <img
              v-if="tab.activeImage"
              :src="tab.key === getActiveTabKey(section.idx, section.tabs) ? tab.activeImage : (tab.inactiveImage || tab.activeImage)"
              class="group-tab-icon"
              :alt="tab.name"
            />
            <span v-else class="group-tab-text">{{ tab.name }}</span>
          </div>
        </div>

        <div v-if="section.type === 'product'" class="products-grid">
          <product-card
            v-for="product in getActiveTab(section.idx, section.tabs).subItems"
            :key="product.id"
            :product="product"
            @click="goToProductDetail"
          />
        </div>
        <div v-else class="articles-list">
          <article-card
            v-for="article in getActiveTab(section.idx, section.tabs).subItems"
            :key="article.id"
            :article="article"
            @click="goToArticleDetail"
          />
        </div>
      </div>
    </template>

    <div style="height:80px"></div>
  </div>
</template>

<script>
import { getPageFull } from '@/api/browse'
import { navigate } from '@/utils/navigate'
import { getUrlMerchantId } from '@/utils/storage'
import ProductCard from '@/components/product-card/product-card.vue'
import ArticleCard from '@/components/article-card/article-card.vue'

export default {
  name: 'Home',
  components: { ProductCard, ArticleCard },
  data() {
    return {
      rawSections: [],
      bannerActiveIndex: {},
      tabActiveKey: {},
      loading: false
    }
  },
  computed: {
    // 严格按后端返回的 sections 数组顺序逐个渲染（后端已按 SectionSort/Sort 排好序），
    // 不按 position 分桶合并——同一 position 出现多次时各自独立成块，保留原始交替顺序
    renderSections() {
      return this.rawSections
        .map((section, idx) => this.normalizeSection(section, idx))
        .filter(Boolean)
    }
  },
  mounted() {
    // 只针对裸 "/" 路由（无 :merchantId 参数）拦截：完全没有商户上下文（当前及历史都没存过）时，
    // 引导到落地页而不是尝试加载首页数据；带商户参数的 /home/:merchantId 不受影响
    if (this.$route.name === 'Index' && !getUrlMerchantId()) {
      this.$router.replace('/landing')
      return
    }
    this.loadHomeData()
  },
  methods: {
    async loadHomeData() {
      this.loading = true
      try {
        const res = await getPageFull()
        this.rawSections = res.data.sections || []
      } catch (error) {
        console.error('加载首页数据失败:', error)
      } finally {
        this.loading = false
      }
    },
    // position 约定：home_banner=轮播图 home_nav=分类导航 home_product=产品列表 home_article=资讯列表（见 CLAUDE.md）
    normalizeSection(section, idx) {
      const items = section.items || []
      if (section.position === 'home_banner') {
        const bannerItems = items.filter(i => i && i.image)
        return bannerItems.length ? { idx, type: 'banner', items: bannerItems } : null
      }
      if (section.position === 'home_nav') {
        const navItems = items.filter(i => i && !this.isInactiveVariant(i))
        return navItems.length ? { idx, type: 'nav', items: navItems, columns: this.getNavColumns(navItems.length) } : null
      }
      if (section.position === 'home_product' || section.position === 'home_article') {
        const tabs = this.buildTabs(items).filter(t => t.subItems.length > 0)
        return tabs.length ? { idx, type: section.position === 'home_product' ? 'product' : 'article', tabs } : null
      }
      return null
    },
    isInactiveVariant(item) {
      return String(item.configName || '').trim().endsWith('未选中')
    },
    // 一行3/4/5个：数量<3固定一行3个；否则优先能被5整除用5列，再能被4整除用4列，再能被3整除用3列，都不能则默认4列
    getNavColumns(count) {
      if (count < 3) return 3
      if (count % 5 === 0) return 5
      if (count % 4 === 0) return 4
      if (count % 3 === 0) return 3
      return 4
    },
    // 同一 position 内，一个 item 可能带一个"-未选中"配对项（仅提供未选中态图标，无 subItems），
    // 二者代表同一个 tab 的两种图标状态，按去掉"未选中"后缀的名称归并成一个 tab
    buildTabs(items) {
      const groups = {}
      ;(items || []).forEach(item => {
        if (!item) return
        const rawName = String(item.configName || '').trim()
        const baseName = rawName.replace(/-?未选中$/, '').trim()
        if (!baseName) return
        if (!groups[baseName]) {
          groups[baseName] = {
            key: baseName,
            name: baseName,
            sort: typeof item.sort === 'number' ? item.sort : Number.MAX_SAFE_INTEGER,
            activeImage: '',
            inactiveImage: '',
            subItems: []
          }
        }
        const g = groups[baseName]
        g.sort = Math.min(g.sort, typeof item.sort === 'number' ? item.sort : Number.MAX_SAFE_INTEGER)
        if (this.isInactiveVariant(item)) {
          g.inactiveImage = item.image || g.inactiveImage
        } else {
          g.activeImage = item.image || g.activeImage
          if (Array.isArray(item.subItems)) g.subItems = item.subItems
        }
      })
      return Object.values(groups)
        .sort((a, b) => a.sort - b.sort)
        .map(g => ({
          key: g.key,
          name: g.name,
          activeImage: g.activeImage || g.inactiveImage,
          inactiveImage: g.inactiveImage || g.activeImage,
          subItems: g.subItems || []
        }))
    },
    getBannerActive(idx) {
      return this.bannerActiveIndex[idx] || 0
    },
    setBannerActive(idx, bIdx) {
      this.$set(this.bannerActiveIndex, idx, bIdx)
    },
    getActiveTabKey(idx, tabs) {
      return this.tabActiveKey[idx] || (tabs[0] && tabs[0].key)
    },
    getActiveTab(idx, tabs) {
      const key = this.getActiveTabKey(idx, tabs)
      return tabs.find(t => t.key === key) || tabs[0] || { subItems: [] }
    },
    setActiveTab(idx, key) {
      this.$set(this.tabActiveKey, idx, key)
    },
    handleItemClick(item) {
      navigate(this.$router, item.linkType, item.linkValue, item.extraValue)
    },
    goToProductDetail(product) {
      this.$router.push({ name: 'ProductDetail', params: { id: product.id } })
    },
    goToArticleDetail(article) {
      this.$router.push({ name: 'ArticleDetail', params: { id: article.id } })
    }
  }
}
</script>

<style lang="scss" scoped>
.home-page {
  width: 100%;
  min-height: 100vh;
  background: #f9fafb;
}

.home-banner {
  position: relative;
  margin: 12px;
  border-radius: 12px;
  overflow: hidden;
}

.banner-item {
  width: 100%;
}

.banner-image {
  display: block;
  width: 100%;
  height: auto;
}

.banner-dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 10px;
  display: flex;
  justify-content: center;
  gap: 6px;
}

.banner-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  cursor: pointer;

  &.active {
    background: #fff;
    width: 16px;
    border-radius: 3px;
  }
}

.home-nav {
  display: grid;
  gap: 10px;
  margin: 0 12px 12px;
}

.nav-item {
  background: #fff;
  border-radius: 12px;
  padding: 10px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:active {
    transform: scale(0.97);
  }
}

.nav-icon {
  width: 100%;
  height: auto;
}

.nav-name {
  font-size: 11px;
  color: #111827;
  font-weight: 500;
  text-align: center;
}

.home-group {
  margin: 0 12px 12px;
}

.group-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.group-tab {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.group-tab-icon {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.group-tab-text {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 16px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 13px;
}

.group-tab.active .group-tab-text {
  background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
  color: #fff;
}

.products-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.articles-list {
  display: flex;
  flex-direction: column;
}
</style>
